import { generate } from '@ant-design/colors'
import { type HueName, hues } from './tokens/primitives'

export type ThemeColor =
  | 'primary'
  | 'darkTeal'
  | 'blue'
  | 'geekBlue'
  | 'cyan'
  | 'green'
  | 'lime'
  | 'gold'
  | 'orange'
  | 'volcano'
  | 'red'
  | 'magenta'
  | 'purple'

export interface ThemeTokens {
  colorPrimary: string
  colorInfo: string
  components: any
}

const ColorOnBrand = '#ffffff'

type Palette = {
  /** Màu thương hiệu chính. */
  base: string
  /** Sắc đậm hơn — trạng thái hover. */
  hover: string
  /** Sắc đậm nhất — trạng thái active. */
  active: string
  /** Sắc nhạt làm nền mục menu đang chọn / đang trỏ. */
  tint: string
  /** Sắc nhạt vừa cho các trạng thái hover của header bảng. */
  tintStrong: string
}

/**
 * Một khuôn duy nhất cho mọi Theme Color.
 *
 * Trước đây `primary` có đủ token kích thước (chiều cao control, padding ô, bo góc, cỡ chữ)
 * còn red/blue/green chỉ khai mấy màu lẻ — nên đổi màu là đổi luôn cả hình khối giao diện.
 * Giờ mọi theme dùng chung khuôn này, **chỉ khác bảng màu**.
 */
const makeTheme = (p: Palette): ThemeTokens => ({
  colorPrimary: p.base,
  colorInfo: p.base,
  components: {
    Menu: {
      fontSize: 14,
      itemHeight: 44,
      iconMarginInlineEnd: 12,
      iconSize: 20,
      collapsedIconSize: 20,
      itemPaddingInline: 16,
      itemMarginInline: 8,
      itemBorderRadius: 8,
      groupTitleFontSize: 12,
      groupTitleColor: 'var(--vbdt-color-text-secondary)',
      itemSelectedColor: p.base,
      subMenuItemSelectedColor: p.base,
      itemSelectedBg: p.tint,
      itemActiveBg: p.tint,
      colorPrimaryBorder: p.base,
    },
    Button: {
      defaultBg: ColorOnBrand,
      borderRadius: 8,
      borderRadiusLG: 8,
      borderRadiusSM: 6,
      lineWidth: 1,
      lineWidthFocus: 3,
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 28,
      paddingInline: 20,
      primaryShadow: 'none',
      defaultShadow: 'none',
      fontWeight: 500,
      colorPrimary: p.base,
      colorPrimaryHover: p.hover,
      colorPrimaryActive: p.active,
    },
    Input: {
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 28,
      activeShadow: '0',
      errorActiveShadow: '0',
      warningActiveShadow: '0',
    },
    Select: { controlHeight: 40, controlHeightLG: 48, controlHeightSM: 28 },
    DatePicker: { controlHeight: 40, controlHeightLG: 48, controlHeightSM: 28 },
    Table: {
      headerColor: 'var(--vbdt-color-text-secondary)',
      headerBg: 'var(--vbdt-color-bg-sunken)',
      headerFilterHoverBg: 'var(--vbdt-color-bg-hover)',
      headerSortActiveBg: 'var(--vbdt-color-bg-sunken)',
      headerSortHoverBg: 'var(--vbdt-color-bg-hover)',
      headerSplitColor: 'transparent',
      rowHoverBg: p.tint,
      colorPrimaryBorder: p.base,
      cellPaddingInline: 16,
      cellPaddingBlock: 16,
      headerBorderRadius: 8,
      borderRadius: 8,
      fontSize: 14,
    },
    Pagination: {
      colorPrimary: p.base,
      itemActiveBg: p.tint,
    },
    Collapse: {
      headerBg: p.tint,
      colorTextHeading: 'var(--vbdt-color-text-primary)',
      marginSM: 'calc(40% + 24px)',
      borderRadiusLG: 8,
    },
    Modal: {
      headerBg: 'transparent',
      titleFontSize: 16,
    },
    Card: {
      borderRadiusLG: 8,
    },
  },
})

/**
 * Mỗi Theme Color chỉ cần **một mã màu gốc**; các sắc hover / active / nền nhạt do
 * `generate()` của `@ant-design/colors` suy ra (cùng thuật toán antd dùng cho bảng màu
 * chuẩn). Trước đây phải khai tay 5 sắc cho mỗi theme — thêm màu là chép 5 dòng và dễ lệch.
 *
 * `generate(base)` trả 10 sắc: `[0]` nhạt nhất … `[5]` chính là màu gốc … `[9]` đậm nhất.
 */
export const themeFrom = (base: string): ThemeTokens => {
  const p = generate(base)
  return makeTheme({ base: p[5], hover: p[6], active: p[7], tint: p[0], tintStrong: p[3] })
}

/** Màu gốc mỗi Theme Color. `primary` = `hues.brand` (USER chốt 2026-09-29), còn lại theo bảng màu chuẩn AntD. */
const THEME_COLOR_BASE: Record<ThemeColor, string> = {
  primary: hues.brand,
  darkTeal: '#00786f',
  blue: '#1677ff',
  geekBlue: '#2f54eb',
  cyan: '#13c2c2',
  green: '#52c41a',
  lime: '#a0d911',
  gold: '#faad14',
  orange: '#fa8c16',
  volcano: '#fa541c',
  red: '#f5222d',
  magenta: '#eb2f96',
  purple: '#722ed1',
}

const TOKENS = Object.fromEntries(
  Object.entries(THEME_COLOR_BASE).map(([k, hex]) => [k, themeFrom(hex)])
) as Record<ThemeColor, ThemeTokens>

/** Token AntD (màu + khuôn component) của một Theme Color. */
export const themeTokens = (color: ThemeColor): ThemeTokens => TOKENS[color]

/** Nhãn hiển thị cho từng Theme Color — dùng ở Drawer thiết lập bố cục. */
export const THEME_COLOR_LABEL: Record<ThemeColor, string> = {
  primary: 'Theo logo',
  darkTeal: 'Xanh ngọc đậm',
  blue: 'Xanh dương',
  geekBlue: 'Xanh chàm',
  cyan: 'Xanh lơ',
  green: 'Xanh lá',
  lime: 'Xanh chanh',
  gold: 'Vàng',
  orange: 'Cam',
  volcano: 'Cam đỏ',
  red: 'Đỏ',
  magenta: 'Hồng',
  purple: 'Tím',
}

/** Theme Color ánh xạ sang hue của design system; màu không khai = `brand`. */
const HUE_BY_THEME_COLOR: Partial<Record<ThemeColor, HueName>> = {
  primary: 'brand',
  red: 'brand',
  orange: 'brand',
  blue: 'info',
  green: 'success',
}

export const hueOf = (color: ThemeColor): HueName => HUE_BY_THEME_COLOR[color] ?? 'brand'
